"use client"
import React, { useContext, useState, useEffect, useCallback } from "react";

interface KeyboardContextType {
    pressedKeys: Set<string>;
    isKeyPressed: (key: string) => boolean;
    areKeysPressed: (keys: string[]) => boolean;
    isAnyKeyPressed: (keys: string[]) => boolean;
    shouldPreventDefault: boolean;
    setShouldPreventDefault: (prevent: boolean) => void;
}

const KeyboardContext = React.createContext<KeyboardContextType>({
    pressedKeys: new Set(),
    isKeyPressed: () => false,
    areKeysPressed: () => false,
    isAnyKeyPressed: () => false,
    shouldPreventDefault: false,
    setShouldPreventDefault: () => {},
});

export function useKeyboardContext() {
    return useContext(KeyboardContext);
}

export function KeyboardContextProvider({ children }: { children: React.ReactNode }) {
    const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());
    const [shouldPreventDefault, setShouldPreventDefault] = useState(false);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (shouldPreventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }

        setPressedKeys(prev => {
            const newKeys = new Set(prev);
            newKeys.add(event.key?.toLowerCase());
            return newKeys;
        });
    }, [shouldPreventDefault]);

    const handleKeyUp = useCallback((event: KeyboardEvent) => {
        if (shouldPreventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }

        setPressedKeys(prev => {
            const newKeys = new Set(prev);
            newKeys.delete(event.key?.toLowerCase());
            return newKeys;
        });
    }, [shouldPreventDefault]);

    const handleWindowBlur = useCallback(() => {
        // Clear all pressed keys when window loses focus
        setPressedKeys(new Set());
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
        window.addEventListener('keyup', handleKeyUp, true); // Use capture phase
        window.addEventListener('blur', handleWindowBlur);

        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            window.removeEventListener('keyup', handleKeyUp, true);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [handleKeyDown, handleKeyUp, handleWindowBlur]);

    const isKeyPressed = useCallback((key: string) => {
        return pressedKeys.has(key?.toLowerCase());
    }, [pressedKeys]);

    const areKeysPressed = useCallback((keys: string[]) => {
        return keys.every(key => pressedKeys.has(key?.toLowerCase()));
    }, [pressedKeys]);

    const isAnyKeyPressed = useCallback((keys: string[]) => {
        return keys.some(key => pressedKeys.has(key?.toLowerCase()));
    }, [pressedKeys]);

    const contextValue: KeyboardContextType = {
        pressedKeys,
        isKeyPressed,
        areKeysPressed,
        isAnyKeyPressed,
        shouldPreventDefault,
        setShouldPreventDefault,
    };

    return (
        <KeyboardContext.Provider value={contextValue}>
            {children}
        </KeyboardContext.Provider>
    );
}